import type { GridColDef } from "@mui/x-data-grid-premium";
import StatusChip from "@entrilia/shared/components/StatusChip";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { WorkbookRun } from "../../../../api/types/workbookRuns.types";
import StepsProgress from "../../../common/StepsProgress";
import WorkbookRunDuration from "./WorkbookRunDuration";
import WorkbookRunsGridActionCell from "./WorkbookRunsGridActionCell";
import { getStatusColor, getStatusLabel } from "./workbookRunUtils";

interface Params {
  downloadingIds: string[];
  cancelingIds: string[];
  getRunPath: (id: string) => string;
  onDownload: (id: string) => void;
  onCancel: (run: WorkbookRun) => void;
  onDelete: (ids: string[]) => void;
}

export const getColumnDefinitions = ({
  downloadingIds,
  cancelingIds,
  getRunPath,
  onDownload,
  onCancel,
  onDelete,
}: Params): GridColDef<WorkbookRun>[] => [
  {
    field: nameof<WorkbookRun>("name"),
    headerName: "Name",
    flex: 3,
    minWidth: 240,
    renderCell: ({ row }) => <GridTextHoverLink to={getRunPath(row.id)} text={row.name} />,
  },
  {
    field: nameof<WorkbookRun>("status"),
    headerName: "Status",
    flex: 0.5,
    minWidth: 120,
    renderCell: ({ row }) => (
      <StatusChip label={getStatusLabel(row.status)} withDot color={getStatusColor(row.status)} />
    ),
  },
  {
    field: "reportsUpdated",
    headerName: "Reports updated",
    flex: 1,
    minWidth: 140,
    renderCell: ({ row }) => <StepsProgress completed={row.processedItems} total={row.reportsSettings.length} />,
    sortable: false,
  },
  {
    field: nameof<WorkbookRun>("templateName"),
    headerName: "Workbook Template",
    flex: 2,
    minWidth: 200,
    renderCell: ({ row }) => <TypographyTooltipEllipsis text={row.templateName} />,
  },
  {
    field: nameof<WorkbookRun>("createdBy"),
    headerName: "Started by",
    flex: 1,
    minWidth: 140,
    renderCell: ({ row }) => <TypographyTooltipEllipsis text={row.createdBy} />,
  },
  {
    field: nameof<WorkbookRun>("createdAt"),
    headerName: "Created at",
    flex: 1,
    minWidth: 160,
    valueGetter: (_, row) => convertISODateTime(row.createdAt),
  },
  {
    field: nameof<WorkbookRun>("startedAt"),
    headerName: "Started at",
    flex: 1,
    minWidth: 160,
    valueGetter: (_, row) => (row.startedAt ? convertISODateTime(row.startedAt) : null),
  },
  {
    field: "duration",
    headerName: "Duration",
    flex: 0.5,
    minWidth: 90,
    renderCell: ({ row }) => <WorkbookRunDuration workbookRun={row} showSeconds={true} />,
    sortable: false,
  },
  {
    field: "actions",
    headerName: "Actions",
    sortable: false,
    width: 144,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row }) => (
      <WorkbookRunsGridActionCell
        downloadingIds={downloadingIds}
        cancelingIds={cancelingIds}
        workbookRun={row}
        onDownload={() => onDownload(row.id)}
        onCancel={() => onCancel(row)}
        onDelete={() => onDelete([row.id])}
      />
    ),
  },
];
